import api from './client';

/**
 * Lawyer Bookings API calls
 */
export const lawyerBookingsAPI = {
  /**
   * Get all bookings accessed by the lawyer
   */
  getBookings: async () => {
    const response = await api.get('/api/bookings/lawyer/');
    return response.data;
  },

  /**
   * Look up a booking by PNR and lastname
   */
  lookupBooking: async (pnr: string, lastname: string, options: any = {}) => {
    const response = await api.post('/api/bookings/lawyer/lookup/', {
      pnr,
      lastname,
      enable_monitoring: options.enableMonitoring || false,
      monitoring_frequency: options.monitoringFrequency || 'daily',
      monitoring_until: options.monitoringUntil || null,
    });
    return response.data;
  },

  /**
   * Get booking detail
   */
  getBookingDetail: async (accessId: number) => {
    const response = await api.get(`/api/bookings/lawyer/${accessId}/`);
    return response.data;
  },

  /**
   * Update monitoring settings
   */
  updateMonitoring: async (accessId: number, settings: any) => {
    const response = await api.patch(`/api/bookings/lawyer/${accessId}/update/`, settings);
    return response.data;
  },

  /**
   * Mark booking as read (clear unread indicator)
   */
  markAsRead: async (accessId: number) => {
    const response = await api.post(`/api/bookings/lawyer/${accessId}/mark-read/`);
    return response.data;
  },

  /**
   * Search bookings by PNR or lastname
   */
  search: async (query: string) => {
    const response = await api.get('/api/bookings/lawyer/search/', {
      params: { q: query }
    });
    return response.data;
  },

  /**
   * Trigger a refresh of booking data
   */
  refreshBooking: async (accessId: number) => {
    const response = await api.post(`/api/bookings/lawyer/${accessId}/refresh/`);
    return response.data;
  },

  /**
   * Get snapshot request status
   */
  getRequestStatus: async (requestId: number) => {
    const response = await api.get(`/api/bookings/lawyer/request-status/${requestId}/`);
    return response.data;
  },

  /**
   * Get booking data at a specific version
   * @param {number} accessId - ID of the LawyerBookingAccess record
   * @param {string} version - Version to fetch: 'base', 'current', or delta sequence number
   * @returns {Promise<{data: object, dictionaries: object}>}
   */
  getBookingVersion: async (accessId: number, version: string) => {
    const response = await api.get(`/api/bookings/lawyer/${accessId}/version/${version}/`);
    return response.data;
  },

  /**
   * Remove a booking from the lawyer's list
   * @param {number} accessId - ID of the LawyerBookingAccess record
   */
  removeBooking: async (accessId: number) => {
    const response = await api.delete(`/api/bookings/lawyer/${accessId}/delete/`);
    return response.data;
  },
};

/**
 * Lawyer Cases API calls
 */
export interface LawyerCaseCreateData {
  mandant_id: number;
  beklagter_id: number;
  custom_title?: string;
}

export const lawyerCasesAPI = {
  /**
   * Get all cases (for lawyers/staff)
   */
  getCases: async () => {
    const response = await api.get('/api/cases/lawyer/');
    return response.data;
  },

  /**
   * Create a new case (for lawyers/staff)
   * Requires mandant_id and beklagter_id (Kontakt IDs)
   */
  createCase: async (data: LawyerCaseCreateData) => {
    const response = await api.post('/api/cases/lawyer/create/', data);
    return response.data;
  },

  /**
   * Get single case detail (for lawyers/staff)
   */
  getCaseById: async (caseId: number) => {
    const response = await api.get(`/api/cases/lawyer/${caseId}/`);
    return response.data;
  },

  /**
   * Update case (for lawyers/staff)
   */
  updateCase: async (caseId: number, caseData: any) => {
    const response = await api.put(`/api/cases/lawyer/${caseId}/`, caseData);
    return response.data;
  },

  /**
   * Delete case (for lawyers/staff)
   */
  deleteCase: async (caseId: number) => {
    const response = await api.delete(`/api/cases/lawyer/${caseId}/`);
    return response.data;
  },

  /**
   * Search cases (magic search across all case data)
   * @param {string} query - Search query (min 2 chars)
   */
  search: async (query: string) => {
    const response = await api.get(`/api/cases/lawyer/search/?q=${encodeURIComponent(query)}`);
    return response.data;
  },

  // Passenger management
  createPassenger: async (caseId: number, data: any) => {
    const response = await api.post(`/api/cases/lawyer/${caseId}/passengers/`, data);
    return response.data;
  },
  updatePassenger: async (caseId: number, passengerId: number, data: any) => {
    const response = await api.put(`/api/cases/lawyer/${caseId}/passengers/${passengerId}/`, data);
    return response.data;
  },
  deletePassenger: async (caseId: number, passengerId: number) => {
    const response = await api.delete(`/api/cases/lawyer/${caseId}/passengers/${passengerId}/`);
    return response.data;
  },

  // Flight segment management
  createSegment: async (caseId: number, data: any) => {
    const response = await api.post(`/api/cases/lawyer/${caseId}/segments/`, data);
    return response.data;
  },
  updateSegment: async (caseId: number, segmentId: number, data: any) => {
    const response = await api.put(`/api/cases/lawyer/${caseId}/segments/${segmentId}/`, data);
    return response.data;
  },
  deleteSegment: async (caseId: number, segmentId: number) => {
    const response = await api.delete(`/api/cases/lawyer/${caseId}/segments/${segmentId}/`);
    return response.data;
  },

  /**
   * Get case activity (history of changes)
   * @param {number} caseId - Case ID
   */
  getCaseActivity: async (caseId: number) => {
    return await api.get(`/api/cases/lawyer/${caseId}/activity/`);
  },

  /**
   * Get messages for a case
   * @param {number} caseId - Case ID
   */
  getMessages: async (caseId: number) => {
    const response = await api.get(`/api/cases/${caseId}/messages/`);
    return response.data;
  },

  /**
   * Send a message
   * @param {number} caseId - Case ID
   * @param {string} messageText - Message content
   */
  sendMessage: async (caseId: number, messageText: string) => {
    const response = await api.post(`/api/cases/${caseId}/messages/`, {
      message_text: messageText,
    });
    return response.data;
  },

  /**
   * Mark all client messages as read for a case
   * @param {number} caseId - Case ID
   */
  markMessagesRead: async (caseId: number) => {
    const response = await api.post(`/api/cases/${caseId}/messages/mark-read/`);
    return response.data;
  },
};

/**
 * Flight Status API calls
 */
export const flightStatusAPI = {
  /**
   * Get flight information via Amadeus FLIFO command
   * @param {string} airline - 2-letter airline code (e.g., "LH")
   * @param {string} flightNumber - Flight number (e.g., "123")
   * @param {string} date - Date in DDMMM format (e.g., "15DEC")
   */
  getFlightStatus: async (airline: string, flightNumber: string, date: string) => {
    const response = await api.post('/api/chat/flight-status/', {
      airline,
      flight_number: flightNumber,
      date,
    });
    return response.data;
  },
};

/**
 * Amadeus Console API calls
 */
export const amadeusConsoleAPI = {
  /**
   * Execute an arbitrary Amadeus cryptic command
   * @param {string} command - The cryptic command (e.g., "RT", "DOLH123/15DEC")
   */
  executeCommand: async (command: string): Promise<{ command: string; response: string | null; error?: string }> => {
    const response = await api.post('/api/chat/amadeus-command/', { command });
    return response.data;
  },
};

/**
 * Types for availability responses
 */
export interface BookingClass {
  code: string;
  seats: number | null;
  is_waitlist: boolean;
}

export interface FlightSegment {
  line_number: number;
  airline: string;
  flight_number: string;
  codeshare_indicator: string | null;
  marketing_airline: string | null;
  operating_airline: string | null;
  departure_airport: string;
  departure_terminal: string | null;
  arrival_airport: string;
  arrival_terminal: string | null;
  departure_time: string;
  arrival_time: string;
  arrival_next_day: boolean;
  equipment: string | null;
  duration: string | null;
  electronic_ticket: boolean;
  is_train: boolean;
  flags: string[];
  booking_classes: BookingClass[];
}

export interface FlightOption {
  option_number: number;
  segments: FlightSegment[];
  total_duration: string | null;
}

export interface AvailabilityResponse {
  command: string;
  raw_response: string;
  origin: string;
  destination: string;
  destination_city: string | null;
  destination_country: string | null;
  date: string | null;
  day_of_week: string | null;
  time: string | null;
  has_more: boolean;
  flights: FlightOption[];
  error?: string;
}

/**
 * Availability Search API calls
 */
export const availabilityAPI = {
  /**
   * Search flight availability via Amadeus AN command
   * @param {string} origin - 3-letter IATA airport code
   * @param {string} destination - 3-letter IATA airport code
   * @param {string} date - Date in DDMMM format (e.g., "15JAN") - optional
   * @param {string} airline - 2-letter IATA airline code - optional
   */
  search: async (
    origin: string,
    destination: string,
    date?: string,
    airline?: string
  ): Promise<AvailabilityResponse> => {
    const response = await api.post('/api/chat/availability/', {
      origin,
      destination,
      date: date || '',
      airline: airline || '',
    });
    return response.data;
  },
};

/**
 * Types for seat map responses
 */
export interface SeatInfo {
  row: number;
  column: string;
  status: string;
  status_code: string;
  status_label: string;
  bookable: boolean;
  features: string[];
}

export interface SeatRow {
  row_number: number;
  cabin_class: string;
  is_wing: boolean;
  is_exit: boolean;
  is_bulkhead: boolean;
  is_upper_deck: boolean;
  seats: SeatInfo[];
}

export interface CabinSection {
  cabin_class: string;
  columns: string[];
  aisle_after: string[];
  rows: SeatRow[];
}

export interface SeatMapLegend {
  [key: string]: {
    code: string;
    label: string;
    bookable: boolean;
  };
}

export interface SeatMapResponse {
  command: string;
  raw_response: string;
  airline: string;
  flight_number: string;
  booking_class: string;
  date: string;
  origin: string;
  destination: string;
  aircraft: string;
  has_more: boolean;
  cabin_sections: CabinSection[];
  legend: SeatMapLegend;
  error?: string;
}

/**
 * Types for FQP pricing responses
 */
export interface FQPFare {
  id: string;
  farebase: string;
  price: number;
}

export interface FQPMeta {
  success: boolean;
  fares_found: number;
  message: string;
}

export interface FQPPricingResponse {
  command: string;
  meta: FQPMeta;
  fares: FQPFare[];
  raw_response: string;
  fqq?: {
    meta: FQPMeta;
    fares: FQPFare[];
    raw_response: string;
  };
  error?: string;
}

/**
 * FQP Pricing API calls
 */
export const fqpPricingAPI = {
  /**
   * Execute FQP fare pricing command
   * Uses the warm Amadeus session maintained by Celery Beat
   * @param command - The FQP command string
   * @param includeFqq - Whether to also run FQQ1 for fare details (default: true)
   */
  execute: async (
    command: string,
    includeFqq: boolean = true
  ): Promise<FQPPricingResponse> => {
    const response = await api.post('/api/chat/fqp-pricing/', {
      command,
      include_fqq: includeFqq,
    });
    return response.data;
  },
};

/**
 * Seat Map API calls
 */
export const seatmapAPI = {
  /**
   * Get seat map for a flight via Amadeus SM command
   * @param {string} airline - 2-letter IATA airline code
   * @param {string} flightNumber - Flight number
   * @param {string} bookingClass - Booking class letter (C, J, Y, F, etc.)
   * @param {string} date - Date in DDMMM format (e.g., "20JAN")
   * @param {string} origin - 3-letter IATA airport code
   * @param {string} destination - 3-letter IATA airport code
   */
  search: async (
    airline: string,
    flightNumber: string,
    bookingClass: string,
    date: string,
    origin: string,
    destination: string
  ): Promise<SeatMapResponse> => {
    const response = await api.post('/api/chat/seatmap/', {
      airline,
      flight_number: flightNumber,
      booking_class: bookingClass,
      date,
      origin,
      destination,
    });
    return response.data;
  },
};

/**
 * Types for async operation status
 */
export interface QueueLengths {
  playwright: number | null;
  default: number | null;
  amadeus: number | null;
  monitoring: number | null;
}

export interface PendingOperations {
  snapshot_requests: number | null;
  amadeus_commands: number | null;
}

export interface AmadeusSessionStatus {
  connected: boolean;
  state?: string;
  session_age_seconds?: number;
  error?: string;
}

export interface AsyncStatusResponse {
  timestamp: string;
  celery_queues: QueueLengths;
  pending_operations: PendingOperations;
  amadeus_session: AmadeusSessionStatus;
}

/**
 * Core monitoring API calls
 */
export const coreAPI = {
  /**
   * Get async operation status (queue lengths, pending operations, Amadeus session)
   */
  getAsyncStatus: async (): Promise<AsyncStatusResponse> => {
    const response = await api.get('/api/core/async-status/');
    return response.data;
  },

  /**
   * Get task registry (for documentation/debugging)
   */
  getTaskRegistry: async () => {
    const response = await api.get('/api/core/task-registry/');
    return response.data;
  },

  /**
   * Get timeout configuration
   */
  getTimeouts: async () => {
    const response = await api.get('/api/core/timeouts/');
    return response.data;
  },
};
